/**
 * Chart Type Detection Utility
 * Analyzes query result data structure to suggest the most appropriate chart type
 */

export type ChartType = 'bar' | 'line' | 'pie' | 'area' | 'table' | 'scatter' | 'radar' | 'mixed';

export interface ChartSuggestion {
    type: ChartType;
    confidence: number;
    reason: string;
}

interface ColumnAnalysis {
    name: string;
    isNumeric: boolean;
    isDate: boolean;
    isText: boolean;
    uniqueValues: number;
    sampleValues: any[];
}

/**
 * Analyzes a column to determine its data type characteristics
 */
function analyzeColumn(columnName: string, values: any[]): ColumnAnalysis {
    const sampleSize = Math.min(100, values.length);
    const sample = values.slice(0, sampleSize);
    const uniqueValues = new Set(sample).size;

    let numericCount = 0;
    let dateCount = 0;

    for (const val of sample) {
        if (val === null || val === undefined) continue;

        // Check if numeric
        if (typeof val === 'number' || !isNaN(Number(val))) {
            numericCount++;
        }

        // Check if date
        const dateVal = new Date(val);
        if (!isNaN(dateVal.getTime()) && typeof val === 'string' && /\d{4}-\d{2}-\d{2}/.test(val)) {
            dateCount++;
        }
    }

    const totalNonNull = sample.filter(v => v !== null && v !== undefined).length;

    return {
        name: columnName,
        isNumeric: numericCount / totalNonNull > 0.8,
        isDate: dateCount / totalNonNull > 0.7,
        isText: numericCount / totalNonNull < 0.3,
        uniqueValues,
        sampleValues: sample.slice(0, 5),
    };
}

/**
 * Main function to detect the best chart type for given query results
 */
export function detectChartType(
    columns: string[],
    rows: any[][]
): ChartSuggestion[] {
    if (!columns || !rows || rows.length === 0) {
        return [{
            type: 'table',
            confidence: 1.0,
            reason: 'No data available for visualization'
        }];
    }

    // If too many columns, default to table
    if (columns.length > 10) {
        return [{
            type: 'table',
            confidence: 0.9,
            reason: 'Too many columns for effective visualization'
        }];
    }

    // Analyze each column
    const columnAnalyses: ColumnAnalysis[] = columns.map((col, idx) => {
        const values = rows.map(row => row[idx]);
        return analyzeColumn(col, values);
    });

    const suggestions: ChartSuggestion[] = [];

    // Count column types
    const numericColumns = columnAnalyses.filter(c => c.isNumeric).length;
    const dateColumns = columnAnalyses.filter(c => c.isDate).length;
    const textColumns = columnAnalyses.filter(c => c.isText).length;

    // Pattern 1: One or more categorical + One or more numeric = Bar Chart
    // Handles cases like: [id, name, revenue], [category, value], etc.
    if (textColumns >= 1 && numericColumns >= 1) {
        const categoricalCol = columnAnalyses.find(c => c.isText);
        if (categoricalCol) {
            // Good for bar chart if we have reasonable number of categories
            if (categoricalCol.uniqueValues <= 50 && categoricalCol.uniqueValues >= 2) {
                suggestions.push({
                    type: 'bar',
                    confidence: textColumns === 1 && numericColumns === 1 ? 0.9 : 0.8,
                    reason: `Categorical data (${categoricalCol.name}) with numeric values - ideal for bar chart`
                });
            }
        }
    }

    // Pattern 2: Date + Numeric = Line Chart
    if (dateColumns >= 1 && numericColumns >= 1) {
        suggestions.push({
            type: 'line',
            confidence: 0.9,
            reason: 'Time-series data detected - best visualized as line chart'
        });

        suggestions.push({
            type: 'area',
            confidence: 0.7,
            reason: 'Time-series data can also be shown as area chart'
        });
    }

    // Pattern 3: Categorical + Numeric (for parts of whole) = Pie Chart
    // Works with 3+ columns too, just uses first text and first numeric
    if (textColumns >= 1 && numericColumns >= 1) {
        const categoricalCol = columnAnalyses.find(c => c.isText);
        if (categoricalCol && categoricalCol.uniqueValues >= 2 && categoricalCol.uniqueValues <= 10) {
            suggestions.push({
                type: 'pie',
                confidence: 0.7,
                reason: `${categoricalCol.uniqueValues} categories - suitable for pie chart distribution`
            });
        }
    }

    // Pattern 3b: Add area chart for categorical + numeric data
    if (textColumns >= 1 && numericColumns >= 1 && rows.length >= 3) {
        suggestions.push({
            type: 'area',
            confidence: 0.65,
            reason: 'Categorical data with numeric values can be shown as area chart'
        });
    }

    // Pattern 4: Multiple numeric columns (no clear x-axis) = Bar chart
    if (numericColumns >= 2 && textColumns >= 1) {
        suggestions.push({
            type: 'bar',
            confidence: 0.8,
            reason: 'Multiple numeric values across categories'
        });
    }

    // Pattern 5: Complex data or many rows = Table
    if (rows.length > 100 || columns.length > 5) {
        suggestions.push({
            type: 'table',
            confidence: 0.8,
            reason: 'Complex dataset - table view recommended'
        });
    }

    // Always include table as fallback
    if (suggestions.length === 0 || !suggestions.find(s => s.type === 'table')) {
        suggestions.push({
            type: 'table',
            confidence: 0.6,
            reason: 'Default tabular view'
        });
    }

    // Sort by confidence
    return suggestions.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Transform query results into format suitable for Recharts
 */
export interface ChartData {
    data: any[];
    xKey: string;
    yKeys: string[];
}

export function transformDataForChart(
    columns: string[],
    rows: any[][]
): ChartData {
    // Transform rows into objects
    const data = rows.map(row => {
        const obj: any = {};
        columns.forEach((col, idx) => {
            obj[col] = row[idx];
        });
        return obj;
    });

    // Analyze to find best x and y axes
    const columnAnalyses = columns.map((col, idx) => {
        const values = rows.map(row => row[idx]);
        return analyzeColumn(col, values);
    });

    // Find x-axis (categorical or date)
    let xKey = columns[0]; // Default to first column
    const dateCol = columnAnalyses.find(c => c.isDate);
    const textCol = columnAnalyses.find(c => c.isText);

    if (dateCol) {
        xKey = dateCol.name;
    } else if (textCol) {
        xKey = textCol.name;
    }

    // Find y-axes (numeric columns)
    const yKeys = columnAnalyses
        .filter(c => c.isNumeric && c.name !== xKey)
        .map(c => c.name);

    // If no numeric columns found, use all except xKey
    if (yKeys.length === 0) {
        yKeys.push(...columns.filter(c => c !== xKey));
    }

    return {
        data,
        xKey,
        yKeys
    };
}
